import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemeMode = 'light' | 'dark';

export interface ThemePaletteColors {
  primary: string;
  accent: string;
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  text: string;
  muted: string;
}

export interface ThemePalette {
  mode: ThemeMode;
  colors: ThemePaletteColors;
  chartPalette: string[];
}

interface ThemePaletteStorage extends ThemePalette {
  borderStrength?: number;
}

const STORAGE_KEY = 'rizkly.theme.palette.v1';

const DEFAULT_CHART_PALETTE = ['#63f3ab', '#6aa9ff', '#f7b84b', '#ff6b6b', '#9b8cff'];

const LIGHT_THEME: ThemePalette = {
  mode: 'light',
  colors: {
    bg: '#f3f6fb',
    surface: '#ffffff',
    surface2: '#ffffff',
    border: 'rgba(15,23,42,0.08)',
    text: '#000000',
    muted: '#64748b',
    primary: '#6200db',
    accent: '#22c55e',
  },
  chartPalette: [...DEFAULT_CHART_PALETTE],
};

const DARK_THEME: ThemePalette = {
  mode: 'dark',
  colors: {
    /*bg: '#0b1020',
    surface: '#141d33',
    surface2: '#1b2640',
    border: 'rgba(255,255,255,0.12)',
    text: '#f2f5ff',
    muted: '#b3c1e3',
    primary: '#63f3ab',
    accent: '#6aa9ff',*/

    bg: '#0b1020',
    surface: '#141d33',
    surface2: '#1b2640',
    border: 'rgba(255,255,255,0.12)',
    text: '#f2f5ff',
    muted: '#b3c1e3',
    primary: '#6200db',
    accent: '#6aa9ff',
  },
  chartPalette: [...DEFAULT_CHART_PALETTE],
};

const DEFAULT_BORDER_STRENGTH: Record<ThemeMode, number> = {
  light: 0.08,
  dark: 0.1,
};

@Injectable({
  providedIn: 'root',
})
export class ThemePaletteService {
  private readonly paletteSubject = new BehaviorSubject<ThemePalette>(LIGHT_THEME);
  private borderStrength = DEFAULT_BORDER_STRENGTH.light;

  readonly palette$ = this.paletteSubject.asObservable();

  constructor() {
    const stored = this.loadFromStorage();
    this.borderStrength = stored.borderStrength ?? DEFAULT_BORDER_STRENGTH[stored.palette.mode];
    const normalized = this.normalizePalette(stored.palette);
    this.paletteSubject.next(normalized);
    this.apply(normalized);
  }

  get palette(): ThemePalette {
    return this.paletteSubject.value;
  }

  get borderStrengthValue(): number {
    return this.borderStrength;
  }

  setMode(mode: ThemeMode): ThemePalette {
    const base = mode === 'light' ? LIGHT_THEME : DARK_THEME;
    const next: ThemePalette = {
      mode,
      colors: { ...base.colors },
      chartPalette: this.normalizeChartPalette(this.palette.chartPalette),
    };
    this.borderStrength = DEFAULT_BORDER_STRENGTH[mode];
    return this.persist(next);
  }

  updateColors(colors: Partial<ThemePaletteColors>): ThemePalette {
    const next: ThemePalette = {
      ...this.palette,
      colors: {
        ...this.palette.colors,
        ...colors,
      },
    };
    return this.persist(next);
  }

  setChartPalette(palette: string[]): ThemePalette {
    const next: ThemePalette = {
      ...this.palette,
      chartPalette: palette,
    };
    return this.persist(next);
  }

  setBorderStrength(strength: number): ThemePalette {
    this.borderStrength = this.clamp(strength, 0.04, 0.2);
    const next: ThemePalette = {
      ...this.palette,
      colors: {
        ...this.palette.colors,
        border: this.withAlpha(this.palette.colors.text, this.borderStrength),
      },
    };
    return this.persist(next);
  }

  resetDefaults(): ThemePalette {
    const base = this.palette.mode === 'light' ? LIGHT_THEME : DARK_THEME;
    this.borderStrength = DEFAULT_BORDER_STRENGTH[base.mode];
    return this.persist({
      mode: base.mode,
      colors: { ...base.colors },
      chartPalette: [...base.chartPalette],
    });
  }

  applyFromSettings(settings?: any): boolean {
    if (!settings) {
      return false;
    }

    const directPalette = this.parsePalette(settings.themePalette || settings.theme_palette);
    if (directPalette) {
      const merged = this.mergeWithDefaults(directPalette);
      this.borderStrength =
        directPalette.borderStrength ?? DEFAULT_BORDER_STRENGTH[merged.mode];
      this.persist(merged);
      return true;
    }

    const legacy = this.buildFromLegacySettings(settings);
    if (legacy) {
      this.persist(legacy);
      return true;
    }

    return false;
  }

  getStorageSnapshot(): ThemePaletteStorage {
    return {
      ...this.palette,
      borderStrength: this.borderStrength,
    };
  }

  private persist(palette: ThemePalette): ThemePalette {
    const normalized = this.normalizePalette(palette);
    this.paletteSubject.next(normalized);
    this.apply(normalized);

    if (this.canUseStorage()) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...normalized, borderStrength: this.borderStrength })
      );
    }

    return normalized;
  }

  private apply(palette: ThemePalette): void {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(`theme-${palette.mode}`);

    root.style.setProperty('--app-bg', palette.colors.bg);
    root.style.setProperty('--app-surface', palette.colors.surface);
    root.style.setProperty('--app-surface-2', palette.colors.surface2);
    root.style.setProperty('--app-border', palette.colors.border);
    root.style.setProperty('--app-text', palette.colors.text);
    root.style.setProperty('--app-text-muted', palette.colors.muted);
    root.style.setProperty('--app-primary', palette.colors.primary);
    root.style.setProperty('--app-accent', palette.colors.accent);

    root.style.setProperty('--main-ui-color', palette.colors.primary);

    root.style.setProperty('--surface-ground', palette.colors.bg);
    root.style.setProperty('--surface-section', palette.colors.surface);
    root.style.setProperty('--surface-card', palette.colors.surface);
    root.style.setProperty('--surface-overlay', palette.colors.surface2);
    root.style.setProperty('--surface-border', palette.colors.border);
    root.style.setProperty('--text-color', palette.colors.text);
    root.style.setProperty('--text-color-secondary', palette.colors.muted);
    const primaryText = this.getReadableTextColor(palette.colors.primary);
    const highlightBg = this.withAlpha(palette.colors.primary, 0.16);
    root.style.setProperty('--primary-color', palette.colors.primary);
    root.style.setProperty('--primary-color-text', primaryText);
    root.style.setProperty('--highlight-bg', highlightBg);
    root.style.setProperty('--highlight-text-color', primaryText);
    root.style.setProperty('--app-highlight-bg', highlightBg);
    root.style.setProperty('--app-highlight-text', primaryText);

    root.style.setProperty('--mat-tab-inactive-label-text-color', highlightBg)
    root.style.setProperty('--p-paginator-background', palette.colors.bg);
    root.style.setProperty('--p-paginator-color', palette.colors.text);
    root.style.setProperty('--p-paginator-nav-button-background', palette.colors.bg);
    root.style.setProperty('--p-paginator-nav-button-hover-background', palette.colors.surface2);
    root.style.setProperty('--p-paginator-nav-button-selected-background', palette.colors.primary);
    root.style.setProperty('--p-paginator-nav-button-color', palette.colors.muted);
    root.style.setProperty('--p-content-background', palette.colors.surface);

    root.style.setProperty('--p-content-color', palette.colors.text);
    root.style.setProperty('--p-content-background', palette.colors.bg);

    root.style.setProperty('--p-highlight-background', palette.colors.primary);

    root.style.setProperty('--mat-form-field-filled-container-color', 'transparent');
    root.style.setProperty('--p-inputtext-background', 'transparent');
    root.style.setProperty('--p-panelmenu-item-color', palette.colors.text);
    root.style.colorScheme = palette.mode;
  }

  private loadFromStorage(): { palette: ThemePalette; borderStrength?: number } {
    if (!this.canUseStorage()) {
      return { palette: LIGHT_THEME, borderStrength: DEFAULT_BORDER_STRENGTH.light };
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { palette: LIGHT_THEME, borderStrength: DEFAULT_BORDER_STRENGTH.light };
    }

    try {
      const parsed = JSON.parse(raw) as ThemePaletteStorage;
      if (!parsed || !parsed.colors) {
        return { palette: LIGHT_THEME, borderStrength: DEFAULT_BORDER_STRENGTH.light };
      }

      return {
        palette: this.mergeWithDefaults(parsed),
        borderStrength: parsed.borderStrength,
      };
    } catch {
      return { palette: LIGHT_THEME, borderStrength: DEFAULT_BORDER_STRENGTH.light };
    }
  }

  private mergeWithDefaults(palette: Partial<ThemePalette>): ThemePalette {
    const mode = palette.mode === 'light' ? 'light' : 'dark';
    const base = mode === 'light' ? LIGHT_THEME : DARK_THEME;
    return {
      mode,
      colors: {
        ...base.colors,
        ...(palette.colors || {}),
      },
      chartPalette: this.normalizeChartPalette(
        palette.chartPalette || base.chartPalette
      ),
    };
  }

  private normalizePalette(palette: ThemePalette): ThemePalette {
    const merged = this.mergeWithDefaults(palette);
    const colors = this.normalizeColors(merged.colors, merged.mode);
    return {
      ...merged,
      colors,
      chartPalette: this.normalizeChartPalette(merged.chartPalette),
    };
  }

  private normalizeColors(colors: ThemePaletteColors, mode: ThemeMode): ThemePaletteColors {
    const base = mode === 'light' ? LIGHT_THEME.colors : DARK_THEME.colors;
    const normalized: ThemePaletteColors = {
      primary: this.normalizeColor(colors.primary, base.primary),
      accent: this.normalizeColor(colors.accent, base.accent),
      bg: this.normalizeColor(colors.bg, base.bg),
      surface: this.normalizeColor(colors.surface, base.surface),
      surface2: this.normalizeColor(colors.surface2, base.surface2),
      border: colors.border || this.withAlpha(colors.text, this.borderStrength),
      text: this.normalizeColor(colors.text, base.text),
      muted: this.normalizeColor(colors.muted, base.muted),
    };

    const textSafe = this.ensureTextContrast(normalized.bg, normalized.text);
    normalized.text = textSafe;
    normalized.muted = this.ensureMutedContrast(normalized.bg, normalized.muted, normalized.text);

    normalized.surface = this.ensureSurfaceContrast(
      normalized.surface,
      normalized.text,
      normalized.bg,
      mode
    );
    normalized.surface2 = this.ensureSurfaceContrast(
      normalized.surface2,
      normalized.text,
      normalized.bg,
      mode
    );

    normalized.border = this.withAlpha(normalized.text, this.borderStrength);

    return normalized;
  }

  private normalizeChartPalette(palette: string[] | undefined): string[] {
    const source = Array.isArray(palette) ? palette : [];
    return DEFAULT_CHART_PALETTE.map((fallback, index) =>
      this.normalizeColor(source[index], fallback)
    );
  }

  private parsePalette(value: unknown): ThemePaletteStorage | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'object') {
      return value as ThemePaletteStorage;
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && parsed.colors) {
          return parsed as ThemePaletteStorage;
        }
      } catch {
        return null;
      }
    }

    return null;
  }

  private buildFromLegacySettings(settings: any): ThemePalette | null {
    const bg = settings.backgroundColor || settings.backgroundColour;
    const palette = this.parseChartPalette(settings.chartPalette || settings.chart_palette);
    const uiColor = settings.uiColor || settings.primaryColor;

    if (!bg && !palette && !uiColor) {
      return null;
    }

    const base = this.palette;
    return {
      mode: base.mode,
      colors: {
        ...base.colors,
        bg: bg || base.colors.bg,
        primary: uiColor || base.colors.primary,
      },
      chartPalette: palette || base.chartPalette,
    };
  }

  private parseChartPalette(value: unknown): string[] | null {
    if (!value) {
      return null;
    }

    if (Array.isArray(value)) {
      return value as string[];
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed as string[];
        }
      } catch {
        const split = value
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean);
        if (split.length) {
          return split;
        }
      }
    }

    return null;
  }

  private ensureTextContrast(bg: string, text: string): string {
    const ratio = this.contrastRatio(bg, text);
    if (ratio >= 3) {
      return text;
    }

    return this.getReadableTextColor(bg);
  }

  private ensureMutedContrast(bg: string, muted: string, text: string): string {
    const ratio = this.contrastRatio(bg, muted);
    if (ratio >= 2.2) {
      return muted;
    }

    return this.withAlpha(text, 0.65);
  }

  private ensureSurfaceContrast(surface: string, text: string, bg: string, mode: ThemeMode): string {
    const ratio = this.contrastRatio(surface, text);
    if (ratio >= 2.2) {
      return surface;
    }

    const tweak = mode === 'dark' ? 0.06 : -0.06;
    return this.adjustLuminance(bg, tweak);
  }

  private normalizeColor(value: string | undefined, fallback: string): string {
    if (!value) {
      return fallback;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return fallback;
    }

    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }

  private adjustLuminance(hexColor: string, amount: number): string {
    const rgb = this.hexToRgb(hexColor);
    if (!rgb) {
      return hexColor;
    }

    const adjust = (channel: number) =>
      this.clamp(Math.round(channel + 255 * amount), 0, 255);

    return `#${this.toHex(adjust(rgb.r))}${this.toHex(adjust(rgb.g))}${this.toHex(adjust(rgb.b))}`;
  }

  private withAlpha(hexColor: string, alpha: number): string {
    const rgb = this.hexToRgb(hexColor);
    if (!rgb) {
      return `rgba(255,255,255,${alpha})`;
    }

    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  }

  private getReadableTextColor(hexColor: string): string {
    const rgb = this.hexToRgb(hexColor);
    if (!rgb) {
      return '#ffffff';
    }

    const luminance = this.relativeLuminance(rgb.r, rgb.g, rgb.b);
    return luminance > 0.45 ? '#0f172a' : '#ffffff';
  }

  private contrastRatio(color1: string, color2: string): number {
    const rgb1 = this.hexToRgb(color1);
    const rgb2 = this.hexToRgb(color2);
    if (!rgb1 || !rgb2) {
      return 1;
    }

    const lum1 = this.relativeLuminance(rgb1.r, rgb1.g, rgb1.b) + 0.05;
    const lum2 = this.relativeLuminance(rgb2.r, rgb2.g, rgb2.b) + 0.05;
    return lum1 > lum2 ? lum1 / lum2 : lum2 / lum1;
  }

  private relativeLuminance(r: number, g: number, b: number): number {
    const srgb = [r, g, b].map((value) => {
      const chan = value / 255;
      return chan <= 0.03928 ? chan / 12.92 : Math.pow((chan + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  }

  private hexToRgb(color: string): { r: number; g: number; b: number } | null {
    if (color.startsWith('rgb')) {
      return null;
    }

    const hex = color.replace('#', '').trim();
    if (hex.length !== 6 && hex.length !== 3) {
      return null;
    }

    const normalized =
      hex.length === 3
        ? hex
            .split('')
            .map((value) => value + value)
            .join('')
        : hex;

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);

    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return null;
    }

    return { r, g, b };
  }

  private toHex(value: number): string {
    return value.toString(16).padStart(2, '0');
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private canUseStorage(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }
}
